import { type ReactNode } from "react";

import { assignInlineVars } from "@vanilla-extract/dynamic";

import * as theme from "./ChartLegend.css";

export type ChartLegendLayout = keyof typeof theme.root;

export interface ChartLegendItem {
    key: string;
    /** A node, so callers can wrap the term in a tooltip or a link of their own. */
    label: ReactNode;
    value: number;
    color: string;
    href?: string;
}

export interface ChartLegendProps {
    items: readonly ChartLegendItem[];
    /** `list` stacks divider-separated rows; `inline` wraps them across one row. */
    layout: ChartLegendLayout;
    /** Rendered as the first row, ahead of the items. */
    heading?: ReactNode;
    onNavigate?: (href: string) => void;
}

/** Swatch / label / value rows describing the series of a chart. */
export function ChartLegend({
    items,
    layout,
    heading,
    onNavigate,
}: ChartLegendProps) {
    return (
        <ul className={theme.root[layout]}>
            {heading !== undefined && (
                <li className={theme.heading[layout]}>{heading}</li>
            )}
            {items.map((item) => (
                <ChartLegendRow
                    key={item.key}
                    item={item}
                    layout={layout}
                    onNavigate={onNavigate}
                />
            ))}
        </ul>
    );
}

interface ChartLegendRowProps {
    item: ChartLegendItem;
    layout: ChartLegendLayout;
    onNavigate?: (href: string) => void;
}

function ChartLegendRow({ item, layout, onNavigate }: ChartLegendRowProps) {
    const { label, value, color, href } = item;
    const content = (
        <>
            <span
                className={theme.swatch[layout]}
                style={assignInlineVars({ [theme.colorVar]: color })}
                aria-hidden
            />
            <span className={theme.label[layout]}>{label}</span>
            <span className={theme.value[layout]}>
                {value.toLocaleString()}
            </span>
        </>
    );

    return (
        <li className={theme.item[layout]}>
            {href === undefined ? (
                <div className={theme.content[layout]}>{content}</div>
            ) : (
                <a
                    className={theme.content[layout]}
                    href={href}
                    onClick={
                        onNavigate
                            ? (event) => {
                                  event.preventDefault();
                                  onNavigate(href);
                              }
                            : undefined
                    }
                >
                    {content}
                </a>
            )}
        </li>
    );
}
